import React from 'react';
import { HiSun, HiMoon, HiComputerDesktop } from 'react-icons/hi2';
import { useDarkMode, type Theme } from '../../stores/themeStore';

interface ThemeMode {
  value: Theme;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}

const themeModes: ThemeMode[] = [
  {
    value: 'light',
    label: 'Light',
    icon: HiSun,
    title: 'Light mode - Always use light theme',
  },
  {
    value: 'system',
    label: 'System',
    icon: HiComputerDesktop,
    title: 'System mode - Follow your OS preference',
  },
  {
    value: 'dark',
    label: 'Dark',
    icon: HiMoon,
    title: 'Dark mode - Always use dark theme',
  },
];

export interface ThemeToggleProps {
  className?: string;
  showLabels?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = '',
  showLabels = true,
  size = 'md',
}) => {
  const { theme, setTheme } = useDarkMode();

  const sizeClasses = {
    sm: {
      button: 'px-2 py-1 text-xs',
      icon: 'h-3 w-3',
      gap: 'gap-1',
    },
    md: {
      button: 'px-3 py-2 text-sm',
      icon: 'h-4 w-4',
      gap: 'gap-2',
    },
    lg: {
      button: 'px-4 py-3 text-base',
      icon: 'h-5 w-5',
      gap: 'gap-3',
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div className={`inline-flex items-center ${currentSize.gap} ${className}`}>
      {showLabels && (
        <span className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300 hidden sm:inline">
          Theme:
        </span>
      )}
      
      <div className="inline-flex rounded-xtnd border border-xtnd-light dark:border-xtnd-dark-700 bg-background-light dark:bg-background-dark p-1 shadow-xtnd">
        {themeModes.map((mode) => {
          const Icon = mode.icon;
          const isActive = theme === mode.value;
          
          return (
            <button
              key={mode.value}
              onClick={() => setTheme(mode.value)}
              title={mode.title}
              className={`
                inline-flex items-center ${currentSize.gap} ${currentSize.button} rounded-md
                font-medium transition-all duration-200 ease-in-out
                ${
                  isActive
                    ? 'bg-xtnd-blue text-xtnd-white shadow-sm'
                    : 'text-xtnd-dark-600 hover:text-xtnd-dark-800 hover:bg-xtnd-light/50 dark:text-xtnd-dark-300 dark:hover:text-xtnd-white dark:hover:bg-xtnd-dark-700'
                }
                focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 
                dark:focus:ring-offset-xtnd-dark-800
              `}
            >
              <Icon className={currentSize.icon} />
              {showLabels && (
                <span className="hidden sm:inline">{mode.label}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemeToggle; 